import {
	axiosWithAuth,
	getFullName,
	convertDateToISO,
	TIME_OFFSET_DAY_END,
} from "helpers";
import {
	ageMinUnderwriting,
	ageMaxUnderwriting,
	ageMinPend,
	ageMaxPend,
	ageMaxApproved,
	ageMinApproved,
} from "mappers";

export const underwritingQueueService = {
	getUnderwritingQueueUnassigned,
	getUnderwritingQueueInReview,
	getUnderwritingQueuePended,
	getUnderwritingQueueHistorical,
	getUnderwritingQueueConcurrenceConditions,
	getAssignedTo,
	claimToCurrentUser,
	unclaimToCurrentUser,
	claimToCurrentUserOnConcurrence,
	unclaimCurrentUserOnConcurrence,
	claimToCurrentUserForRejected,
	getUnderwritingQueueQAUnassigned,
	getUnderwritingQueueQAInProgress,
	getUnderwritingQueueQACompleted,
	claimToCurrentUserOnQaUw,
	unclaimCurrentUserOnQaUw,
};

const PAGE_SIZE = 50;
const API_URL = process.env.REACT_APP_API_URL;

function getUnderwritingQueueUnassigned({ page, sortBy, filtersApplied }) {
	return axiosWithAuth({
		method: "GET",
		url: `${API_URL}/api/underwriting/applications/unassigned`,
		params: {
			page_index: page,
			page_size: PAGE_SIZE,
			sort_type: sortBy?.id ? sortBy?.id : undefined,
			ascending: sortBy?.id ? sortBy?.desc : undefined,
			// Filters
			number: filtersApplied?.number,
			merchant_name: filtersApplied?.merchantName,
			processor_id: filtersApplied?.processor?.id,
			sponsor_id: filtersApplied?.sponsor?.id,
			mcc_code: filtersApplied?.mccCode,
			high_risk: filtersApplied?.highRisk?.value,
			agent_office: filtersApplied?.agentOffice?.name,
			agent_name:
				getFullName(filtersApplied?.agent) !== ""
					? getFullName(filtersApplied?.agent)
					: undefined,
			age_in_underwriting_from: ageMinUnderwriting(filtersApplied),
			age_in_underwriting_to: ageMaxUnderwriting(filtersApplied),
		},
	});
}

function getUnderwritingQueueInReview({ page, sortBy, filtersApplied }) {
	return axiosWithAuth({
		method: "GET",
		url: `${API_URL}/api/underwriting/applications/in_review`,
		params: {
			page_index: page,
			page_size: PAGE_SIZE,
			sort_type: sortBy?.id ? sortBy?.id : undefined,
			ascending: sortBy?.id ? sortBy?.desc : undefined,
			// Filters
			assignee_id: filtersApplied?.assigned_to?.id,
			number: filtersApplied?.number,
			merchant_name: filtersApplied?.merchantName,
			processor_id: filtersApplied?.processor?.id,
			sponsor_id: filtersApplied?.sponsor?.id,
			mcc_code: filtersApplied?.mccCode,
			high_risk: filtersApplied?.highRisk?.value,
			agent_office: filtersApplied?.agentOffice?.name,
			agent_name:
				getFullName(filtersApplied?.agent) !== ""
					? getFullName(filtersApplied?.agent)
					: undefined,
			age_in_underwriting_from: ageMinUnderwriting(filtersApplied),
			age_in_underwriting_to: ageMaxUnderwriting(filtersApplied),
		},
	});
}

function getUnderwritingQueuePended({ page, sortBy, filtersApplied }) {
	return axiosWithAuth({
		method: "GET",
		url: `${API_URL}/api/underwriting/applications/pended`,
		params: {
			page_index: page,
			page_size: PAGE_SIZE,
			sort_type: sortBy?.id ? sortBy?.id : undefined,
			ascending: sortBy?.id ? sortBy?.desc : undefined,
			// Filters
			underwriting_assignee_id:
				filtersApplied?.assigned_to?.id !== "unassigned"
					? filtersApplied?.assigned_to?.id
					: undefined,
			underwriting_unassigned:
				filtersApplied?.assigned_to?.id === "unassigned"
					? true
					: undefined,
			number: filtersApplied?.number,
			merchant_name: filtersApplied?.merchantName,
			processor_id: filtersApplied?.processor?.id,
			sponsor_id: filtersApplied?.sponsor?.id,
			mcc_code: filtersApplied?.mccCode,
			high_risk: filtersApplied?.highRisk?.value,
			agent_office: filtersApplied?.agentOffice?.name,
			agent_name:
				getFullName(filtersApplied?.agent) !== ""
					? getFullName(filtersApplied?.agent)
					: undefined,
			pend_reason_id: filtersApplied?.pendReason?.id,
			pend_status: filtersApplied?.pendStatus?.name,
			pended_to_id: filtersApplied?.pendedTo?.id,
			age_in_underwriting_from: ageMinUnderwriting(filtersApplied),
			age_in_underwriting_to: ageMaxUnderwriting(filtersApplied),
			pended_from: ageMinPend(filtersApplied),
			pended_to: ageMaxPend(filtersApplied),
		},
	});
}

function getUnderwritingQueueHistorical({ page, sortBy, filtersApplied }) {
	return axiosWithAuth({
		method: "GET",
		url: `${API_URL}/api/underwriting/applications/historical`,
		params: {
			page_index: page,
			page_size: PAGE_SIZE,
			sort_type: sortBy?.id ? sortBy?.id : undefined,
			ascending: sortBy?.id ? sortBy?.desc : undefined,
			// Filters
			number: filtersApplied?.number,
			merchant_name: filtersApplied?.merchantName,
			processor_id: filtersApplied?.processor?.id,
			sponsor_id: filtersApplied?.sponsor?.id,
			mcc_code: filtersApplied?.mccCode,
			high_risk: filtersApplied?.highRisk?.value,
			agent_office: filtersApplied?.agentOffice?.name,
			agent_name:
				getFullName(filtersApplied?.agent) !== ""
					? getFullName(filtersApplied?.agent)
					: undefined,
			age_in_underwriting_from: ageMinUnderwriting(filtersApplied),
			age_in_underwriting_to: ageMaxUnderwriting(filtersApplied),
			underwriting_completed_from: convertDateToISO(
				filtersApplied?.dateComplete?.[0],
			),
			underwriting_completed_to: convertDateToISO(
				filtersApplied?.dateComplete?.[1],
				{ addTime: TIME_OFFSET_DAY_END },
			),
			approved_by_id: filtersApplied?.approved_by?.id,
			underwriting_decision: filtersApplied?.underwriting_decision?.name,
			underwriting_completed_by_id: filtersApplied?.complete_by?.id,
		},
	});
}

function getUnderwritingQueueConcurrenceConditions({
	page,
	sortBy,
	filtersApplied,
}) {
	return axiosWithAuth({
		method: "GET",
		url: `${API_URL}/api/underwriting/applications/concurrence_conditions`,
		params: {
			page_index: page,
			page_size: PAGE_SIZE,
			sort_type: sortBy?.id ? sortBy?.id : undefined,
			ascending: sortBy?.id ? sortBy?.desc : undefined,
			// Filters
			concurrence_assignee_id:
				filtersApplied?.assigned_to?.id !== "unassigned"
					? filtersApplied?.assigned_to?.id
					: undefined,
			concurrence_unassigned:
				filtersApplied?.assigned_to?.id === "unassigned"
					? true
					: undefined,
			number: filtersApplied?.number,
			merchant_name: filtersApplied?.merchantName,
			processor_id: filtersApplied?.processor?.id,
			sponsor_id: filtersApplied?.sponsor?.id,
			mcc_code: filtersApplied?.mccCode,
			high_risk: filtersApplied?.highRisk?.value,
			agent_office: filtersApplied?.agentOffice?.name,
			agent_name:
				getFullName(filtersApplied?.agent) !== ""
					? getFullName(filtersApplied?.agent)
					: undefined,
			age_in_underwriting_from: ageMinUnderwriting(filtersApplied),
			age_in_underwriting_to: ageMaxUnderwriting(filtersApplied),
			age_in_underwriting_approved_from: ageMinApproved(filtersApplied),
			age_in_underwriting_approved_to: ageMaxApproved(filtersApplied),
			approved_by_id: filtersApplied?.approved_by?.id,
		},
	});
}

function getAssignedTo(page) {
	const options = {
		method: "POST",
		data: { page, pageSize: PAGE_SIZE },
		url: `${API_URL}/api/underwriting/assigned-to`,
	};

	return axiosWithAuth(options);
}

function claimToCurrentUser({ id }) {
	const options = {
		method: "POST",
		url: `${API_URL}/api/underwriting/${id}/assign`,
	};

	return axiosWithAuth(options);
}

function unclaimToCurrentUser({ id }) {
	const options = {
		method: "POST",
		url: `${API_URL}/api/underwriting/${id}/unassign`,
	};

	return axiosWithAuth(options);
}

function claimToCurrentUserOnConcurrence({ id }) {
	const options = {
		method: "POST",
		url: `${API_URL}/api/underwriting/applications/concurrence_conditions/${id}/assign`,
	};

	return axiosWithAuth(options);
}

function unclaimCurrentUserOnConcurrence({ id }) {
	const options = {
		method: "POST",
		url: `${API_URL}/api/underwriting/applications/concurrence_conditions/${id}/unassign`,
	};

	return axiosWithAuth(options);
}

function claimToCurrentUserForRejected({ id }) {
	const options = {
		method: "POST",
		url: `${API_URL}/api/underwriting/applications/auto_rejected/${id}/assign`,
	};

	return axiosWithAuth(options);
}

function getUnderwritingQueueQAUnassigned({ page, sortBy, filtersApplied }) {
	return axiosWithAuth({
		method: "GET",
		url: `${API_URL}/api/underwriting/qa/applications/unassigned`,
		params: {
			page_index: page,
			page_size: PAGE_SIZE,
			sort_type: sortBy?.id ? sortBy?.id : undefined,
			ascending: sortBy?.id ? sortBy?.desc : undefined,
			// Filters
			underwriting_qa_assignee_id:
				filtersApplied?.assigned_to?.id !== "unassigned"
					? filtersApplied?.assigned_to?.id
					: undefined,
			number: filtersApplied?.application?.number,
			merchant_name: filtersApplied?.merchantName,
			processor_id: filtersApplied?.processor?.id,
			sponsor_id: filtersApplied?.sponsor?.id,
			mcc_code: filtersApplied?.mccCode,
			is_high_risk: filtersApplied?.highRisk?.value,
			agent_office: filtersApplied?.agentOffice?.name,
			agent_name:
				getFullName(filtersApplied?.agent) !== ""
					? getFullName(filtersApplied?.agent)
					: undefined,
			age_in_underwriting_qa_from: ageMinUnderwriting(filtersApplied),
			age_in_underwriting_qa_to: ageMaxUnderwriting(filtersApplied),
			approved_by_id: filtersApplied?.approved_by?.id,
		},
	});
}

function getUnderwritingQueueQAInProgress({ page, sortBy, filtersApplied }) {
	return axiosWithAuth({
		method: "GET",
		url: `${API_URL}/api/underwriting/qa/applications/in_progress`,
		params: {
			page_index: page,
			page_size: PAGE_SIZE,
			sort_type: sortBy?.id ? sortBy?.id : undefined,
			ascending: sortBy?.id ? sortBy?.desc : undefined,
			// Filters
			underwriting_qa_assignee_id:
				filtersApplied?.assigned_to?.id !== "unassigned"
					? filtersApplied?.assigned_to?.id
					: undefined,
			number: filtersApplied?.application?.number,
			merchant_name: filtersApplied?.merchantName,
			processor_id: filtersApplied?.processor?.id,
			sponsor_id: filtersApplied?.sponsor?.id,
			mcc_code: filtersApplied?.mccCode,
			is_high_risk: filtersApplied?.highRisk?.value,
			agent_office: filtersApplied?.agentOffice?.name,
			agent_name:
				getFullName(filtersApplied?.agent) !== ""
					? getFullName(filtersApplied?.agent)
					: undefined,
			age_in_underwriting_qa_from: ageMinUnderwriting(filtersApplied),
			age_in_underwriting_qa_to: ageMaxUnderwriting(filtersApplied),
			approved_by_id: filtersApplied?.approved_by?.id,
		},
	});
}

function getUnderwritingQueueQACompleted({ page, sortBy, filtersApplied }) {
	return axiosWithAuth({
		method: "GET",
		url: `${API_URL}/api/underwriting/qa/applications/completed`,
		params: {
			page_index: page,
			page_size: PAGE_SIZE,
			sort_type: sortBy?.id ? sortBy?.id : undefined,
			ascending: sortBy?.id ? sortBy?.desc : undefined,
			// Filters
			underwriting_qa_assignee_id:
				filtersApplied?.assigned_to?.id !== "unassigned"
					? filtersApplied?.assigned_to?.id
					: undefined,
			number: filtersApplied?.application?.number,
			merchant_name: filtersApplied?.merchantName,
			processor_id: filtersApplied?.processor?.id,
			sponsor_id: filtersApplied?.sponsor?.id,
			mcc_code: filtersApplied?.mccCode,
			is_high_risk: filtersApplied?.highRisk?.value,
			agent_office: filtersApplied?.agentOffice?.name,
			agent_name:
				getFullName(filtersApplied?.agent) !== ""
					? getFullName(filtersApplied?.agent)
					: undefined,
			age_in_underwriting_qa_from: ageMinUnderwriting(filtersApplied),
			age_in_underwriting_qa_to: ageMaxUnderwriting(filtersApplied),
			underwriting_qa_completed_by_id: filtersApplied?.complete_by?.id,
			underwriting_qa_completed_from: convertDateToISO(
				filtersApplied?.dateComplete?.[0],
			),
			underwriting_qa_completed_to: convertDateToISO(
				filtersApplied?.dateComplete?.[1],
				{ addTime: TIME_OFFSET_DAY_END },
			),
		},
	});
}

function claimToCurrentUserOnQaUw({ id }) {
	const options = {
		method: "POST",
		url: `${API_URL}/api/underwriting/qa/applications/${id}/assign`,
	};

	return axiosWithAuth(options);
}

function unclaimCurrentUserOnQaUw({ id }) {
	const options = {
		method: "POST",
		url: `${API_URL}/api/underwriting/qa/applications/${id}/unassign`,
	};

	return axiosWithAuth(options);
}
